(() => {
  if (document.documentElement.dataset.mjRoomCarousel) return;
  document.documentElement.dataset.mjRoomCarousel = 'true';
  const instances = new WeakMap();
  const HOLD = 8000;
  const TURN = 1500;

  function init(root) {
    if (root.dataset.mjRoomMobile) return;
    if (instances.has(root)) return;
    const choices = [...root.querySelectorAll('.mj-room-choice')];
    if (!choices.length) { root.hidden = true; return; }
    const canvas = root.querySelector('.mj-room-canvas');
    const dialog = root.querySelector('dialog');
    const phone = root.querySelector('.mj-room-phone');
    const status = root.querySelector('.mj-room-ar-status');
    const picker = root.querySelector('.mj-room-picker');
    const browse = root.querySelector('.mj-room-browse');
    const toggle = root.querySelector('.mj-room-tour-toggle');
    const position = root.querySelector('.mj-room-position');
    const qr = JSON.parse(root.querySelector('.mj-room-qr-data').textContent);
    const wallUrls = JSON.parse(root.querySelector('.mj-room-wall-data').textContent);
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const listen = (target, type, fn, options = {}) => target.addEventListener(type, fn, { ...options, signal: controller.signal });
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const android = /Android/i.test(ua);
    const mobile = ios || android || /Mobile/i.test(ua);
    const deep = new URLSearchParams(location.search).get('room');
    let active = choices.find(b => b.dataset.index === deep) || choices[0];
    let started = false, destroyed = false, visible = false, desired = !motion.matches && !deep;
    let current = null, staged = null, outgoing = null, cycleTimer = 0, turnTimer = 0, cameraTimer = 0;
    let due = false, turning = false, animations = [];
    const failed = new Set();
    const running = () => desired && visible && !document.hidden && !destroyed;
    const eligible = () => choices.filter(b => !b.hidden && !failed.has(b.dataset.index));
    const roomUrl = b => location.origin + '/?room=' + encodeURIComponent(b.dataset.index) + '#' + root.id;

    function mobileHelp() {
      status.hidden = false;
      status.textContent = root.dataset.arHelp;
    }

    function configureLaunch(b) {
      phone.removeAttribute('rel');
      phone.dataset.launch = 'viewer';
      phone.href = roomUrl(b);
      status.hidden = true;
      const image = document.createElement('img');
      image.src = b.dataset.poster;
      image.alt = '';
      image.width = 1;
      image.height = 1;
      image.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0';
      phone.append(image);
      const usdz = wallUrls[b.dataset.index] || b.dataset.usdz;
      if (ios && usdz) {
        phone.rel = 'ar';
        phone.href = new URL(usdz, location.origin).href;
        phone.dataset.launch = 'quick-look';
      } else if (android && b.dataset.glb) {
        const q = new URLSearchParams({
          file: new URL(b.dataset.glb, location.origin).href,
          mode: 'ar_preferred', title: b.dataset.title,
          link: new URL(b.dataset.url, location.origin).href
        });
        if (b.dataset.category === 'wall') q.set('enable_vertical_placement', 'true');
        const fallback = new URL(roomUrl(b));
        fallback.searchParams.set('ar_unavailable', '1');
        phone.href = 'intent://arvr.google.com/scene-viewer/1.2?' + q + '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=' + encodeURIComponent(fallback.href) + ';end;';
        phone.dataset.launch = 'scene-viewer';
      }
      if (new URLSearchParams(location.search).has('ar_unavailable')) mobileHelp();
    }

    function qrMarkup(id) {
      const q = qr[id];
      if (!q) return '';
      const bytes = atob(q.bits);
      let path = '';
      for (let j = 0; j < q.n * q.n; j++) {
        if (bytes.charCodeAt(j >> 3) & (1 << (7 - (j % 8)))) path += 'M' + (j % q.n) + ' ' + Math.floor(j / q.n) + 'h1v1h-1z';
      }
      return '<svg viewBox="0 0 ' + q.n + ' ' + q.n + '" role="img" aria-label="' + root.dataset.qrLabel + '" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="white"/><path d="' + path + '" fill="#003b36"/></svg>';
    }

    function updateCopy(b) {
      active = b;
      choices.forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      root.querySelector('.mj-room-name').textContent = b.dataset.title;
      root.querySelector('.mj-room-price').textContent = b.dataset.price;
      root.querySelector('.mj-room-product').href = b.dataset.url;
      root.querySelector('.mj-room-dialog-link').href = roomUrl(b);
      root.querySelector('.mj-room-qr').innerHTML = qrMarkup(b.dataset.index);
      phone.textContent = b.dataset.category === 'wall' ? root.dataset.wallLabel : root.dataset.roomLabel;
      configureLaunch(b);
      const visibleChoices = choices.filter(x => !x.hidden);
      const index = visibleChoices.indexOf(b);
      position.textContent = index < 0 ? '' : (index + 1) + ' / ' + visibleChoices.length;
    }

    function removeRecord(record) {
      if (!record) return;
      clearTimeout(record.timeout);
      record.cancelled = true;
      record.mv.removeEventListener('load', record.onLoad);
      record.mv.removeEventListener('error', record.onError);
      record.slot.remove();
    }

    function createRecord(b, preview) {
      const template = root.querySelector('template[data-index="' + b.dataset.index + '"]');
      if (!template) { failed.add(b.dataset.index); return null; }
      const fragment = template.content.cloneNode(true);
      const mv = fragment.querySelector('model-viewer');
      if (!mv) { failed.add(b.dataset.index); return null; }
      const slot = document.createElement('div');
      slot.className = 'mj-room-slot' + (preview ? ' is-staged' : '');
      slot.setAttribute('aria-hidden', String(preview));
      slot.append(fragment);
      const record = { button: b, slot, mv, loaded: false, cancelled: false, timeout: 0 };
      mv.removeAttribute('auto-rotate');
      mv.setAttribute('loading', 'eager');
      mv.setAttribute('reveal', 'auto');
      mv.setAttribute('auto-rotate-delay', '0');
      mv.setAttribute('rotation-per-second', '45deg');
      mv.setAttribute('interaction-prompt', 'none');
      mv.setAttribute('camera-orbit', preview ? '25deg 20deg 120%' : '25deg 75deg 120%');
      mv.setAttribute('interpolation-decay', '200');
      if (b.dataset.glb) mv.setAttribute('src', b.dataset.glb);
      mv.setAttribute('poster', b.dataset.poster);
      mv.setAttribute('ar', '');
      mv.setAttribute('ar-placement', b.dataset.category === 'wall' ? 'wall' : 'floor');
      mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      const usdz = wallUrls[b.dataset.index] || b.dataset.usdz;
      if (usdz) mv.setAttribute('ios-src', usdz);
      if (b.dataset.category === 'wall') mv.setAttribute('shadow-intensity', '0');
      record.onLoad = () => {
        if (record.cancelled || record.loaded) return;
        record.loaded = true;
        clearTimeout(record.timeout);
        if (record === current) sync();
        if (record === staged && due && running()) advance();
      };
      record.onError = () => {
        if (record.cancelled || destroyed) return;
        clearTimeout(record.timeout);
        failed.add(b.dataset.index);
        if (record === staged) {
          staged = null;
          removeRecord(record);
          if (running()) prepareNext();
        } else if (record === current) {
          record.loaded = false;
          mv.removeAttribute('auto-rotate');
          if (!slot.querySelector('.mj-room-empty')) {
            const message = document.createElement('p');
            message.className = 'mj-room-empty';
            message.textContent = root.dataset.loadError;
            slot.append(message);
          }
          due = true;
          if (running()) { prepareNext(); if (staged?.loaded) advance(); }
        }
      };
      mv.addEventListener('load', record.onLoad);
      mv.addEventListener('error', record.onError);
      record.timeout = setTimeout(record.onError, 20000);
      canvas.append(slot);
      if (mv.loaded) queueMicrotask(record.onLoad);
      return record;
    }

    function nextButton() {
      const pool = eligible();
      if (!pool.length) return null;
      const index = pool.indexOf(active);
      const next = pool[(index + 1) % pool.length];
      return next === active ? null : next;
    }

    function prepareNext() {
      if (!running() || turning) return;
      const next = nextButton();
      if (staged && staged.button !== next) { removeRecord(staged); staged = null; }
      if (!next || staged) return;
      staged = createRecord(next, true);
      if (!staged && eligible().length > 1) prepareNext();
    }

    function clearCycle() {
      clearTimeout(cycleTimer);
      cycleTimer = 0;
      due = false;
    }

    function finishTurn() {
      clearTimeout(turnTimer);
      clearTimeout(cameraTimer);
      animations.forEach(animation => animation.cancel());
      animations = [];
      removeRecord(outgoing);
      outgoing = null;
      turning = false;
      if (current) {
        current.slot.classList.remove('is-entering', 'is-staged');
        current.slot.setAttribute('aria-hidden', 'false');
        current.mv.setAttribute('camera-orbit', '25deg 75deg 120%');
        current.mv.setAttribute('interpolation-decay', '200');
      }
    }

    function advance() {
      if (!running() || turning || !staged?.loaded) return;
      clearCycle();
      outgoing = current;
      current = staged;
      staged = null;
      turning = true;
      current.slot.classList.remove('is-staged');
      current.slot.classList.add('is-entering');
      current.slot.setAttribute('aria-hidden', 'false');
      outgoing?.slot.setAttribute('aria-hidden', 'true');
      outgoing?.mv.removeAttribute('auto-rotate');
      updateCopy(current.button);
      if (!motion.matches && current.slot.animate) {
        current.mv.setAttribute('interpolation-decay', '450');
        animations.push(current.slot.animate([
          { transform: 'translate3d(35%,88%,-160px) rotateZ(62deg) rotateX(65deg) scale(.72)', opacity: 0 },
          { transform: 'translate3d(12%,25%,-40px) rotateZ(20deg) rotateX(25deg) scale(.94)', opacity: 1, offset: .6 },
          { transform: 'none', opacity: 1 }
        ], { duration: TURN, easing: 'cubic-bezier(.22,.65,.24,1)', fill: 'both' }));
        if (outgoing) animations.push(outgoing.slot.animate([
          { transform: 'none', opacity: 1 },
          { transform: 'translate3d(-18%,28%,-50px) rotateZ(-28deg) rotateX(25deg) scale(.91)', opacity: .8, offset: .4 },
          { transform: 'translate3d(-80%,90%,-200px) rotateZ(-72deg) rotateX(55deg) scale(.65)', opacity: 0 }
        ], { duration: TURN, easing: 'cubic-bezier(.4,0,.65,1)', fill: 'both' }));
        cameraTimer = setTimeout(() => current?.mv.setAttribute('camera-orbit', '25deg 75deg 120%'), 300);
        turnTimer = setTimeout(() => { finishTurn(); sync(); }, TURN);
      } else { finishTurn(); sync(); }
    }

    function sync() {
      if (destroyed) return;
      if (!root.isConnected) { destroy(); return; }
      toggle.textContent = desired ? root.dataset.pauseLabel : root.dataset.playLabel;
      toggle.setAttribute('aria-pressed', String(desired));
      const rotate = running() && !motion.matches && current?.loaded;
      current?.mv.toggleAttribute('auto-rotate', !!rotate);
      if (!running()) {
        clearCycle();
        if (turning) finishTurn();
        return;
      }
      if (turning) return;
      prepareNext();
      if (due) { if (staged?.loaded) advance(); return; }
      if (current?.loaded && !cycleTimer && nextButton()) {
        cycleTimer = setTimeout(() => {
          cycleTimer = 0;
          due = true;
          prepareNext();
          advance();
        }, HOLD);
      }
    }

    function pause() { desired = false; sync(); }

    function select(b) {
      clearCycle();
      if (turning) finishTurn();
      removeRecord(staged);
      staged = null;
      removeRecord(current);
      current = null;
      updateCopy(b);
      failed.delete(b.dataset.index);
      current = createRecord(b, false);
      sync();
    }

    function start() {
      if (started || destroyed) return;
      started = true;
      select(active);
      if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }]);
    }

    function destroy() {
      destroyed = true;
      clearCycle();
      finishTurn();
      removeRecord(staged);
      removeRecord(current);
      controller.abort();
      observer.disconnect();
      motion.removeEventListener('change', motionChange);
      instances.delete(root);
    }

    listen(toggle, 'click', () => { desired = !desired; start(); sync(); });
    listen(canvas, 'pointerdown', pause);
    listen(canvas, 'wheel', pause, { passive: true });
    listen(canvas, 'keydown', pause);
    listen(canvas, 'camera-change', event => { if (event.detail?.source === 'user-interaction') pause(); });
    listen(root.querySelector('.mj-room-product'), 'click', pause);
    choices.forEach(b => listen(b, 'click', () => {
      pause();
      if (!started) { active = b; start(); } else select(b);
      root.querySelector('.mj-room-stage').scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' });
    }));
    listen(phone, 'click', async event => {
      pause();
      start();
      if (phone.dataset.launch === 'quick-look' || phone.dataset.launch === 'scene-viewer') return;
      event.preventDefault();
      const mv = current?.mv;
      if (mv?.canActivateAR) {
        try { await mv.activateAR(); } catch { if (mobile) mobileHelp(); else dialog.showModal(); }
      } else if (mobile) mobileHelp();
      else dialog.showModal();
    });
    listen(root.querySelector('.mj-room-close'), 'click', () => dialog.close());
    listen(dialog, 'click', e => {
      if (e.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) dialog.close();
    });
    root.querySelector('.mj-room-count').textContent = choices.length;
    root.querySelectorAll('[data-filter]').forEach(b => listen(b, 'click', () => {
      root.querySelectorAll('[data-filter]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      choices.forEach(x => { x.hidden = b.dataset.filter !== 'all' && x.dataset.category !== b.dataset.filter; });
      root.querySelector('.mj-room-count').textContent = choices.filter(x => !x.hidden).length;
      picker.scrollLeft = 0;
      clearCycle();
      const first = choices.find(x => !x.hidden);
      if (first && active.hidden) { start(); select(first); }
      else { updateCopy(active); sync(); }
    }));
    listen(browse, 'click', () => {
      const expanded = picker.classList.toggle('is-expanded');
      browse.setAttribute('aria-expanded', String(expanded));
      browse.textContent = expanded ? root.dataset.collapseLabel : root.dataset.browseLabel;
    });
    listen(document, 'visibilitychange', sync);
    const motionChange = () => { if (motion.matches) desired = false; sync(); };
    motion.addEventListener('change', motionChange);
    const observer = new IntersectionObserver(entries => {
      visible = entries.some(e => e.isIntersecting);
      if (visible) start();
      sync();
    }, { threshold: .15 });
    observer.observe(canvas);
    instances.set(root, { destroy });
    sync();
    if (deep) { start(); requestAnimationFrame(() => root.scrollIntoView({ block: 'start' })); }
  }

  const boot = scope => scope.querySelectorAll('.mj-room').forEach(init);
  boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.mj-room').forEach(root => instances.get(root)?.destroy());
  });
})();
